/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2022 (original work) Open Assessment Technologies SA ;
 */

import { writable, get } from 'svelte/store';

/**
 * The pendingOperationsStore is used to detect if this item has any operations in progress -
 *   these operations may have side-effects for the test-runner.
 * For example, while images are uploading in RichTextEditor, navigation should be disabled.
 */

//we keep the stores per item
const pendingOperationsStores = new Map();

/**
 * Get the pending operations store
 * @param {string} itemIdentifier
 * @returns {Observable<Object>} pendingOperationsStore
 */
export function getItemPendingOperationsStore(itemIdentifier) {
    if (!itemIdentifier) {
        throw new TypeError('An "itemIdentifier" is required for getItemPendingOperationsStore');
    }

    if (pendingOperationsStores.has(itemIdentifier)) {
        return pendingOperationsStores.get(itemIdentifier);
    }

    const { subscribe, set, update } = writable({ operationKeys: [] });

    const pendingOperationsStore = {
        subscribe,
        set,
        update,

        /**
         * Get the store data
         * @returns {Object}
         */
        get() {
            return get(this);
        },

        /**
         * Add pending operation
         * @param {string} operationKey - globally unique key (generated id)
         */
        add(operationKey) {
            if (operationKey) {
                update(stored => {
                    stored.operationKeys.push(operationKey);
                    return stored;
                });
            }
        },

        /**
         * Delete pending operation
         * @param {string} operationKey
         */
        delete(operationKey) {
            if (operationKey) {
                update(stored => {
                    const idx = stored.operationKeys.indexOf(operationKey);
                    if (idx >= 0) {
                        stored.operationKeys.splice(stored.operationKeys.indexOf(operationKey), 1);
                    }
                    return stored;
                });
            }
        },

        /**
         * Check if there are no pending operations for this item
         * @returns {boolean}
         */
        isEmpty() {
            return get(this).operationKeys.length === 0;
        },

        /**
         * Clears the store
         */
        clear() {
            set({ operationKeys: [] });
        }
    };

    pendingOperationsStores.set(itemIdentifier, pendingOperationsStore);
    return pendingOperationsStore;
}

/**
 * Release the store for this item so next time you'll get a new one.
 * @param {string} itemIdentifier
 */
export function releaseItemPendingOperationsStore(itemIdentifier) {
    if (itemIdentifier && pendingOperationsStores.has(itemIdentifier)) {
        pendingOperationsStores.delete(itemIdentifier);
    }
}
